import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { type ReceivableInvoiceAPI } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  clientId: string;
}

interface QueryResponse {
  data: ReceivableInvoiceAPI[];
  // Can be added if we need pagination
  // meta: {};
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/invoices/shared`;

export function fetchInvoicesKey(clientId: string): QueryKey {
  return ['invoices', clientId] as QueryKey;
}

export async function fetchInvoices(params: Params, customOptions: CustomOptions) {
  const { onError } = customOptions;

  try {
    const response = await qontoClient<QueryResponse>(URL, {
      params: {
        client_id: params.clientId,
        page: {
          number: 1,
          size: 100,
        },
      },
    });

    const normalizedResponse = response.data.map(invoice => ({
      ...invoice.attributes,
      id: invoice.id,
    }));

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchInvoicesResponse = Awaited<ReturnType<typeof fetchInvoices>>;

export function useFetchInvoices(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchInvoicesResponse>>
) {
  return useQuery({
    queryKey: fetchInvoicesKey(params.clientId),
    queryFn: () => fetchInvoices(params, { ...customOptions }),
    ...queryOptions,
  });
}
